/**
 * The sync engine: the state machine and the drain.
 *
 * WRITE FIRST, ALWAYS. enqueueCompletion() writes the record and every photo
 * Blob to IndexedDB and resolves only when they are durable. Then, and only
 * then, it asks for a drain. If the network is gone the drain fails and the
 * record sits at `failed` with a retry time. It is never deleted.
 *
 * DRAINS RUN FROM TWO PLACES - the page (timer, `online`, `visibilitychange`,
 * the driver's own tap) and the service worker (the Background Sync `sync`
 * event). They must not run at once: two uploaders racing the same photo is
 * how a stop double-counts. The Web Locks API serialises them; where it is
 * missing (old WebViews, tests) a module-level flag does the same job for a
 * single context.
 *
 * ORDER. A stop's completion record is uploaded first; its photos only after
 * that succeeded. The server upserts photos independently, but a photo for a
 * stop whose record has not landed is a record with a hole in it.
 *
 * BACKOFF. 5s, 10s, 20s ... capped at 10 minutes, with jitter. A record's
 * counter only moves when it was actually attempted: when the very first
 * request of a drain fails at the network level, the rest are left untouched
 * (`offline`), so airplane mode does not push every stop to a ten-minute wait.
 *
 * NEVER DROP. There is no maximum attempt count and no retention window.
 * A record that keeps failing keeps its error on it and stays visible in the
 * queue indicator until a person or the network fixes it.
 */
import { getCompletion, getPhoto, listCompletions, listFindings, listPhotosForStop, putCompletion, putFinding, putPhoto, setMeta } from './db'
import { ApiError, NetworkError, postCompletion, postReferencePhoto, putFoundNote, putPhoto as uploadPhoto } from './api'
import type { CompletionPayload, CompletionRecord, DrainResult, FindingRecord, PhotoRecord } from './types'

export const BACKOFF_BASE_MS = 5_000
export const BACKOFF_CAP_MS = 10 * 60_000
export const LOCK_NAME = 'rbf-sync-drain'
export const SYNC_TAG = 'rbf-stop-sync'
export const CHANNEL_NAME = 'rbf-sync'

export function backoffMs(attempts: number, random: () => number = Math.random): number {
  const exp = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** Math.max(0, attempts - 1))
  const jitter = Math.floor(random() * Math.min(exp, 5_000))
  return Math.min(BACKOFF_CAP_MS, exp + jitter)
}

export function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  // Fallback for very old WebViews: still a UUID v4 shape.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

function notify(detail: unknown): void {
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const ch = new BroadcastChannel(CHANNEL_NAME)
      ch.postMessage(detail)
      ch.close()
    }
  } catch {
    /* a closed channel is not a failed sync */
  }
}

export interface PhotoInput {
  itemType: string
  blob: Blob
  capturedAt: string
}

/**
 * Persist a completion and its photos. Resolves when IndexedDB has them.
 * Photos must already be compressed (photo.ts) - this never touches pixels.
 */
export async function enqueueCompletion(
  stopUuid: string,
  routeId: number,
  payload: CompletionPayload,
  photos: PhotoInput[],
): Promise<CompletionRecord> {
  const now = Date.now()
  const rec: CompletionRecord = {
    stopUuid,
    completionId: newId(),
    routeId,
    payload,
    state: 'queued',
    attempts: 0,
    nextAttemptAt: now,
    lastError: '',
    createdAt: now,
    updatedAt: now,
    syncedAt: null,
  }
  await putCompletion(rec)
  for (const p of photos) {
    const photo: PhotoRecord = {
      key: `${stopUuid}:${p.itemType}`,
      stopUuid,
      itemType: p.itemType,
      blob: p.blob,
      bytes: p.blob.size,
      capturedAt: p.capturedAt,
      state: 'queued',
      attempts: 0,
      nextAttemptAt: now,
      lastError: '',
      syncedAt: null,
    }
    await putPhoto(photo)
  }
  notify({ type: 'enqueued', stopUuid })
  return rec
}

/**
 * The driver-screen path: the checklist stored its photos as 'local' while
 * it was in progress; pressing Complete turns them all 'queued' and writes
 * (or overwrites - a fix) the completion record. One durable step, then done.
 */
export async function enqueueFromLocal(
  stopUuid: string,
  routeId: number,
  payload: CompletionPayload,
): Promise<CompletionRecord> {
  const now = Date.now()
  const existing = await getCompletion(stopUuid)
  const rec: CompletionRecord = {
    stopUuid,
    completionId: newId(), // a fix is a NEW completion
    routeId,
    payload,
    state: 'queued',
    attempts: 0,
    nextAttemptAt: now,
    lastError: '',
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    syncedAt: null,
  }
  await putCompletion(rec)
  for (const photo of await listPhotosForStop(stopUuid)) {
    if (photo.state === 'local') {
      photo.state = 'queued'
      photo.nextAttemptAt = now
      await putPhoto(photo)
    }
  }
  notify({ type: 'enqueued', stopUuid })
  return rec
}

// ── the drain ────────────────────────────────────────────────────────────────

let localLock = false

async function withLock<T>(fn: () => Promise<T>, onBusy: () => T): Promise<T> {
  const locks = (globalThis.navigator as Navigator | undefined)?.locks
  if (locks && typeof locks.request === 'function') {
    return locks.request(LOCK_NAME, { ifAvailable: true }, async (lock) => {
      if (!lock) return onBusy()
      return fn()
    }) as Promise<T>
  }
  if (localLock) return onBusy()
  localLock = true
  try {
    return await fn()
  } finally {
    localLock = false
  }
}

function describe(err: unknown): string {
  if (err instanceof ApiError) return `HTTP ${err.status}: ${err.message}`
  if (err instanceof NetworkError) return `network: ${err.message}`
  return err instanceof Error ? err.message : String(err)
}

/**
 * Write a record's sync state back - unless the driver replaced the record
 * while the upload was in flight. A "fix last stop" during a drain produces
 * a NEW completionId (or a retaken photo a new capturedAt); writing the stale
 * copy over it would silently undo the fix. Found by the drafts test, 2026-09-09.
 */
async function commitState(rec: CompletionRecord | PhotoRecord): Promise<boolean> {
  if ('payload' in rec) {
    const current = await getCompletion(rec.stopUuid)
    if (current && current.completionId !== rec.completionId) return false
    await putCompletion(rec)
    return true
  }
  const current = await getPhoto(rec.key)
  if (current && current.capturedAt !== rec.capturedAt) return false
  await putPhoto(rec)
  return true
}

async function markFailed(rec: CompletionRecord | PhotoRecord, err: unknown, now: number): Promise<void> {
  rec.attempts += 1
  rec.state = 'failed'
  rec.lastError = describe(err)
  rec.nextAttemptAt = now + backoffMs(rec.attempts)
  if ('payload' in rec) rec.updatedAt = now
  await commitState(rec)
}

export interface DrainOptions {
  source?: DrainResult['source']
  now?: () => number
}

/**
 * Upload everything that is due. Safe to call from anywhere, any time; a
 * concurrent call returns immediately with skippedLock=true.
 */
export async function drain(opts: DrainOptions = {}): Promise<DrainResult> {
  const source = opts.source ?? 'foreground'
  const clock = opts.now ?? Date.now
  const result: DrainResult = { source, attempted: 0, synced: 0, failed: 0, offline: false, skippedLock: false }

  return withLock(
    async () => {
      const due = (await listCompletions())
        .filter((c) => (c.state === 'queued' || c.state === 'failed') && c.nextAttemptAt <= clock())
        .sort((a, b) => a.createdAt - b.createdAt)

      for (const rec of due) {
        if (result.offline) break
        result.attempted += 1
        rec.state = 'syncing'
        rec.updatedAt = clock()
        if (!(await commitState(rec))) continue // replaced by a fix since we listed it
        try {
          await postCompletion(rec)
          rec.state = 'synced'
          rec.syncedAt = clock()
          rec.lastError = ''
          await commitState(rec)
          result.synced += 1
        } catch (err) {
          await markFailed(rec, err, clock())
          result.failed += 1
          if (err instanceof NetworkError) result.offline = true
          continue
        }
        await drainPhotos(rec.stopUuid, result, clock)
      }

      // Photos whose completion synced on an earlier round but which failed
      // themselves (or were re-queued by a fix) are due too.
      if (!result.offline) {
        const syncedStops = (await listCompletions()).filter((c) => c.state === 'synced')
        for (const c of syncedStops) {
          if (result.offline) break
          await drainPhotos(c.stopUuid, result, clock)
        }
      }

      // What drivers found (notes, reference photos). Last, and independent of the
      // stop's completion: a finding is worth keeping on a skipped stop too.
      if (!result.offline) await drainFindings(result, clock)

      await setMeta('lastDrainAt', clock())
      notify({ type: 'drained', result })
      return result
    },
    () => ({ ...result, skippedLock: true }),
  )
}

async function drainPhotos(stopUuid: string, result: DrainResult, clock: () => number): Promise<void> {
  const photos = (await listPhotosForStop(stopUuid)).filter(
    (p) => (p.state === 'queued' || p.state === 'failed') && p.nextAttemptAt <= clock(),
  )
  for (const photo of photos) {
    if (result.offline) return
    result.attempted += 1
    photo.state = 'syncing'
    if (!(await commitState(photo))) continue // retaken since we listed it
    try {
      await uploadPhoto(photo)
      photo.state = 'synced'
      photo.syncedAt = clock()
      photo.lastError = ''
      await commitState(photo)
      result.synced += 1
    } catch (err) {
      await markFailed(photo, err, clock())
      result.failed += 1
      if (err instanceof NetworkError) result.offline = true
    }
  }
}

/** Findings in capture order. Same rules as everything else: never dropped, backoff on
 *  failure, a network failure ends the round without touching the rest. Once uploaded a
 *  photo's bytes are dropped; the record stays so the queue can say it went. */
async function drainFindings(result: DrainResult, clock: () => number): Promise<void> {
  const due = (await listFindings())
    .filter((f: FindingRecord) => (f.state === 'queued' || f.state === 'failed') && f.nextAttemptAt <= clock())
    .sort((a, b) => a.createdAt - b.createdAt)
  for (const rec of due) {
    if (result.offline) return
    result.attempted += 1
    rec.state = 'syncing'
    await putFinding(rec)
    try {
      if (rec.kind === 'note') await putFoundNote(rec)
      else await postReferencePhoto(rec)
      rec.state = 'synced'
      rec.syncedAt = clock()
      rec.lastError = ''
      rec.blob = null
      await putFinding(rec)
      result.synced += 1
    } catch (err) {
      rec.attempts += 1
      rec.state = 'failed'
      rec.lastError = describe(err)
      rec.nextAttemptAt = clock() + backoffMs(rec.attempts)
      await putFinding(rec)
      result.failed += 1
      if (err instanceof NetworkError) result.offline = true
    }
  }
}

/** Re-queue a stop after a "fix last stop": the new record supersedes. */
export async function requeueStop(stopUuid: string): Promise<CompletionRecord | undefined> {
  const rec = await getCompletion(stopUuid)
  if (!rec) return undefined
  rec.state = 'queued'
  rec.nextAttemptAt = Date.now()
  rec.completionId = newId()
  rec.updatedAt = Date.now()
  await putCompletion(rec)
  return rec
}

/** Ask the service worker to drain when connectivity returns (Chrome/Android). */
export async function requestBackgroundSync(): Promise<boolean> {
  try {
    const sw = (globalThis.navigator as Navigator | undefined)?.serviceWorker
    if (!sw) return false
    const reg = await sw.ready
    const sync = (reg as unknown as { sync?: { register: (tag: string) => Promise<void> } }).sync
    if (!sync) return false
    await sync.register(SYNC_TAG)
    return true
  } catch {
    return false
  }
}
